import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';

export default function MFARecoveryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeLogin } = useAuth();
  const mfaToken = location.state?.mfaToken;

  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!mfaToken) {
    navigate('/login', { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/mfa/verify-recovery', {
        mfa_token: mfaToken,
        recovery_code: recoveryCode,
      });

      const { access_token, refresh_token, user } = response.data;
      completeLogin(access_token, refresh_token, user);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Invalid recovery code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Nephr&#333;s</h1>
          <p style={styles.subtitle}>Recovery Code Login</p>
        </div>

        <p style={styles.instructions}>
          Enter one of your saved recovery codes. Each code can only be used once.
        </p>

        {error && <div style={styles.errorBanner}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="recoveryCode">Recovery Code</label>
            <input
              id="recoveryCode"
              type="text"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              placeholder="XXXX-XXXX-XXXX"
              style={{ ...styles.input, letterSpacing: '0.1em', textAlign: 'center' }}
              autoFocus
              required
            />
          </div>

          <Button type="submit" disabled={loading} style={{ width: '100%', marginTop: '4px' }}>
            {loading ? 'Verifying...' : 'Verify Recovery Code'}
          </Button>

          <Button variant="ghost" type="button" onClick={() => navigate('/login')}
            style={{ alignSelf: 'center' }}>
            Back to login
          </Button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-bg-page)', fontFamily: 'var(--font-family-base)' },
  card: { backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', padding: '48px', width: '100%', maxWidth: '400px' },
  header: { textAlign: 'center', marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-nav)', margin: '0 0 4px 0', letterSpacing: '-0.5px', fontFamily: 'var(--font-family-base)' },
  subtitle: { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', margin: 0, fontFamily: 'var(--font-family-base)' },
  instructions: { fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '24px', lineHeight: '1.5', fontFamily: 'var(--font-family-base)' },
  errorBanner: { backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: '13px', color: '#991b1b', marginBottom: '20px', fontFamily: 'var(--font-family-base)' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)' },
  input: { padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', outline: 'none', fontFamily: 'var(--font-family-base)' },
};